import React, { ReactElement, useEffect, useRef } from "react";

import { Card, Divider, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import PostBookingPaymentController from "../PostBookingPaymentController";
import TwoColumnLayout from "../TwoColumnLayout";
import Wrapper from "../../atoms/Wrapper";
import AncillaryBreakdown from "./AncillaryBreakdown";
import { assert } from "../../../utils/assert";
import ConfirmationTestingTools from "../ConfirmationInner/components/ConfirmationTestingTools";
import useEventTracking from "../../../hooks/useEventTracking";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useFormatPrice from "hooks/useFormatPrice";
import { useHistory } from "react-router-dom";
import { PostBookingCheckoutFooterDesktop } from "../PostBookingCheckoutFooter";
import styles from "./PostBookingPaymentInner.desktop.module.css";
import { getAddOnOrder, getOrder } from "store/order/selectors";
import { useSelector } from "react-redux";
import ConditionalWrap from "components/atoms/ConditionalWrap";
import useUserAgent from "hooks/useUserAgent";
import { UnifiedPriceBreakdownPostBookingPayment } from "components/elements/UnifiedPriceBreakdown";

type Props = {
  postBookingModalPaymentCallback?: () => void;
  postBookingModalBackCallback?: () => void;
};

const PostBookingPaymentInnerDesktop = ({ postBookingModalPaymentCallback, postBookingModalBackCallback }: Props) => {
  const i18n = useI18n();
  const order = useSelector(getOrder);
  const addOnOrder = useSelector(getAddOnOrder);
  const { requestId } = useGlobalContext();
  const history = useHistory();
  const { formatPrice } = useFormatPrice();
  const { isMobile } = useUserAgent();
  assert(order, "No order");
  assert(addOnOrder, "No add-on order");

  const { ancillaries } = addOnOrder;
  assert(ancillaries, "No ancillaries");

  const confirmationFinePrintContent = order?.supplierInfo?.content?.confirmationFinePrint;
  const trackV2 = useEventTracking("pb_checkout_payment", requestId);

  const trackingRef = useRef({
    trackV2,
    ancillaries: addOnOrder.ancillaries
  });

  useEffect(() => {
    const { trackV2, ancillaries } = trackingRef.current;
    const checkedoutAncillaries = ancillaries && Object.keys(ancillaries);
    if (checkedoutAncillaries?.includes("checkedInBaggage") || checkedoutAncillaries?.includes("cabinBaggage")) {
      trackV2("show_pb_payments_page_luggage");
    }
    if (checkedoutAncillaries?.includes("seatMapSelection")) {
      trackV2("show_pb_payments_page_seat");
    }
  }, []);

  return (
    <PostBookingPaymentController postBookingModalPaymentCallback={postBookingModalPaymentCallback}>
      {({ pcContainerRef, onPayClick, pcStatus, pcIsLoaded, getTotalPrice, isFinalizeLoading }) => {
        const totalPrice = getTotalPrice(ancillaries);

        return (
          <>
            <ConfirmationTestingTools />
            <main className={isMobile ? styles.root : ""}>
              <Wrapper noBorder noPaddingVertical={!isMobile} noPadding={!isMobile}>
                <Stack gap={4}>
                  {!!isMobile && (
                    <div>
                      <Text variant="body_2">
                        {i18n.trans(
                          t("flights_apps_step_generic_screen_title", { variables: { step: 2, step_total: 2 } })
                        )}
                      </Text>
                      <Text variant="headline_1">{i18n.trans(t("flights_checkout_review_header"))}</Text>
                    </div>
                  )}
                  <ConditionalWrap
                    condition={!!isMobile}
                    wrap={(children: ReactElement) => (
                      <TwoColumnLayout
                        sidebar={
                          <>
                            <Card>
                              <Stack>
                                <Text variant="strong_1">
                                  {i18n.trans(t("flights_ancillary_confirmation_summary_extras"))}
                                </Text>
                                <AncillaryBreakdown ancillaries={ancillaries} />
                              </Stack>
                            </Card>
                          </>
                        }
                        transparent
                      >
                        {children}
                      </TwoColumnLayout>
                    )}
                  >
                    <>
                      <Stack gap={4}>
                        <Text variant="headline_3" tagName="h2">
                          {i18n.trans(t("flights_price_details_title"))}
                        </Text>
                        <UnifiedPriceBreakdownPostBookingPayment order={order} />

                        <Divider />
                        <Stack gap={4}>
                          <Text variant="strong_1">{i18n.trans(t("flights_checkout_payment"))}</Text>
                          <div ref={pcContainerRef} />
                        </Stack>
                        {confirmationFinePrintContent ||
                          (!!postBookingModalPaymentCallback && (
                            <Stack gap={8}>
                              <Divider />
                              <Stack className={styles.terms}>
                                <Text variant="strong_2">{i18n.trans(t("flights_checkout_conditions"))}</Text>
                                <Text variant="small_1">
                                  {confirmationFinePrintContent ? (
                                    <span dangerouslySetInnerHTML={{ __html: confirmationFinePrintContent }} />
                                  ) : !!postBookingModalPaymentCallback ? (
                                    i18n.trans(t("flights_ace_pb_payment_terms"))
                                  ) : undefined}
                                </Text>
                              </Stack>
                              <Divider />
                            </Stack>
                          ))}
                      </Stack>
                      <PostBookingCheckoutFooterDesktop
                        labelPrimary={
                          !isMobile && postBookingModalBackCallback
                            ? `${i18n.trans(t("flights_checkout_complete_action"))}`
                            : `${i18n.trans(t("flights_checkout_complete_action"))} ${formatPrice(totalPrice.total)}`
                        }
                        isPrimaryEnabled={pcIsLoaded}
                        isPrimaryLoading={pcStatus === "processing" || isFinalizeLoading}
                        onClickPrimary={() => {
                          trackV2("click_pb_ancillary_pay_now", {
                            addon_order_id: addOnOrder.addOnOrderId,
                            ancillaries: Object.keys(addOnOrder.ancillaries || [])
                          });
                          onPayClick();
                        }}
                        labelSecondary={i18n.trans(t("flights_action_back"))}
                        onClickSecondary={() => {
                          if (!isMobile && postBookingModalBackCallback) {
                            postBookingModalBackCallback();
                          } else {
                            history.goBack();
                          }
                        }}
                        showBorder={false}
                        pbFastTrackModalContent={
                          !!postBookingModalPaymentCallback ? (
                            <Text variant="headline_3">{formatPrice(totalPrice.total)}</Text>
                          ) : undefined
                        }
                      />
                    </>
                  </ConditionalWrap>
                </Stack>
              </Wrapper>
            </main>
          </>
        );
      }}
    </PostBookingPaymentController>
  );
};

export default PostBookingPaymentInnerDesktop;
